import type { AdDetails } from '@amzn/safeframe-model';
import type { SafeFrameClient } from '../components/safeFrame.js';
import { produceViewableInfo } from '../components/sfAPI.js';
import { CommonApiImplementation } from './CommonApiImplementation.js';
import { ClientMessageReceiver } from './clientMessageReceiver.js';
import { ClientMessageSender } from './clientMessageSender.js';
import { disableCookieAccess, disableGeolocationApi, sendCriticalFeatureAndLoaded } from './commonSf.js';
import * as AD_LOAD_EVENTS from '../components/events/AD_LOAD_CSA_EVENTS.js';
import { IFRAME_INIT } from '../components/counters/AD_LOAD_CSM_COUNTERS.js';
import { BODY_END } from '../components/metrics/latency-metric-type.js';
import { postCreativeWrite, preCreativeWrite, renderHtml } from './render.js';
import { replaceForHtml } from '../components/clickTracking/replace.js';
import { DEFAULT_PERCOLATE_TIMEOUT, waitForPercolateClickTrackingFromHost } from './percolateClickTracker.js';
import {
    InterventionReportError,
    setupReportingObserver,
} from '../components/reportingObserver/setupReportingObserver.js';
import { logHighRateMetricSF } from '@/components/metrics/awsMetricLogger.js';
import { getCreative } from './service/get-creative.js';
import { convertToStringIfBase64 } from '../components/util.js';
import { create$sf } from '@/client/apiPage/sfApi.js';
import { EventsChannel } from '@/components/messageChannel/eventsChannel.js';
import {
    CLICK_TRACKING_PAINTER_TEST,
    SAFEFRAME_DOCUMENT_WRITE,
    SAFEFRAME_ONERROR_COLLAPSE,
} from '@/components/weblabs.js';
import { evaluateCreativeBeginToRender } from '@amzn/creative-rendering-monitor/src/creativeBTREvaluatorSync.js';
import { documentWriteImpl, writeCreativeToDOM } from '@/client/service/documentWrite.js';

declare global {
    interface Window {
        aaxInstrPixelUrl: any;
    }
}

export type CommonSFClientSetupState = {
    hasFiredCODPixel: boolean;
};

type InitSFClientOptions = {
    timeout?: number;
};

export class SafeframeClientImpl<ActualApiImplementation extends CommonApiImplementation> implements SafeFrameClient {
    readonly nativeWrite: typeof document.write;
    renderCompleteTime: Date | null = null;
    state: CommonSFClientSetupState = {
        hasFiredCODPixel: false,
    };

    private clickTrackingParam = ''; // Containers the clickTrackingParam passed from the host

    constructor(
        protected readonly o: AdDetails,
        private readonly mp: MessagePort,
        private readonly cms: ClientMessageSender,
        readonly cmr: ClientMessageReceiver,
        readonly c: ActualApiImplementation,
        private readonly eventsChannel: EventsChannel,
    ) {
        /*
         * Override document.open so we can detect future writes and ensure globals are set
         */
        const originalDocumentOpen = document.open.bind(document);
        document.open = ((...args: Parameters<typeof document.open>) => {
            const result = originalDocumentOpen(...args);
            this.ensureGlobals();
            return result;
        }) as typeof document.open;

        this.nativeWrite = document.write.bind(document);

        window.onerror = this.handleErrors;

        // log heavy ad interventions. See https://developer.chrome.com/blog/heavy-ad-interventions
        setupReportingObserver(this.o, { onReport: this.logInterventionReport });
    }

    logInterventionReport = (error: InterventionReportError) => {
        // log to cloudwatch
        logHighRateMetricSF({
            metricName: 'reporting-observer:heavy-ad-intervention',
            adDetails: this.o,
        });

        // log to RTLA
        this.c.logFatal('heavy-ad-intervention', error);
    };

    initSFClient = async ({ timeout = DEFAULT_PERCOLATE_TIMEOUT }: InitSFClientOptions = {}) => {
        this.ensureGlobals();
        /* Disable creative access to set and get cookies: https://issues.amazon.com/issues/APEX-4377 */
        disableCookieAccess();
        /* Disable geolocation API: https://issues.amazon.com/issues/CPP-24902 */
        disableGeolocationApi();

        /*
         * Called by the creative for further mutations to the DOM
         */
        if (SAFEFRAME_DOCUMENT_WRITE().isT1()) {
            document.write = documentWriteImpl;
        } else {
            document.write = this.documentWriteDelegate;
        }

        // Send clientBodyEnd after setting up the safeframe APIs
        this.c.sendLatencyMetric(BODY_END);
        this.eventsChannel.onSafeFrameReady(this.o);
        // Wait for the Percolate Click Tracking To Come Back
        if (!CLICK_TRACKING_PAINTER_TEST().isT1()) {
            this.clickTrackingParam = await waitForPercolateClickTrackingFromHost(this.mp, timeout);
        }
        // We don't want to start processing messages in the host message receiver until we have gotten the
        // percolate click tracking param
        this.mp.onmessage = this.cmr.receiveMessage;

        this.c.countMetric(IFRAME_INIT, 1);
        this.c.logCsaEvent(AD_LOAD_EVENTS.IFRAME_INIT);

        await this.writeCreativeToDocument();
    };

    /**
     * This should only be called once per iframe. Inserts the creative html into the iframe document's DOM.
     * Currently, we assume all video ad creatives which use the safeframe have a built-in video player.
     * VAST ads should be rendered through LightAds.
     * We expect the synthetic load event to be fired during this code path
     */
    private readonly writeCreativeToDocument = async (): Promise<void> => {
        addSyntheticOnLoadListeners(this, this.o);

        try {
            this.o.isNoInventory = false;
            let htmlContent = replaceForHtml(this.clickTrackingParam, getCreative(this.o));
            htmlContent = this.tryEncapsulateInWrapper(htmlContent);
            preCreativeWrite(this.cms, this.c.cr, this.o, this.eventsChannel, this.state);

            if (SAFEFRAME_DOCUMENT_WRITE().isT1()) {
                await writeCreativeToDOM(htmlContent);
                this.renderCompleteTime = new Date();
            } else {
                await this.documentWriteDelegate(htmlContent);
            }

            this.eventsChannel.onPostCreativeWrite(this.o);
            await postCreativeWrite(this.o, this.cms, this.c.cr, this.c, this.eventsChannel);
            this.eventsChannel.onCreativeLoad(this.o);
            if (CLICK_TRACKING_PAINTER_TEST().isT1()) {
                document.body.addEventListener(
                    'click',
                    () => {
                        this.eventsChannel.onClick(this.o);
                    },
                    { once: true },
                );
            }
        } catch (err) {
            this.c.logError('Failed to get creative content', err as Error);
            this.c.collapseSlotInternal('CLIENTSETUP');
            this.eventsChannel.onError(this.o);
        }
    };

    ensureGlobals = () => {
        window.onerror = this.handleErrors;
        window.$sf = window.$sf ?? create$sf();
        window.$sfViewableInfo = produceViewableInfo();
        window.aaxInstrPixelUrl = this.o.aaxInstrPixelUrl;
    };

    /**
     * This method can be called multiple times during the lifecycle
     */
    documentWriteDelegate = async (htmlContent: string): Promise<void> =>
        await renderHtml(htmlContent, this, this.c, this.o);

    /* Overwrite the window's onerror method to catch any unexpected errors
     * coming from the creative code. We will log the error and attempt to
     * show a different ad or collapse the slot.
     */
    private handleErrors: OnErrorEventHandler = (
        message: Event | string,
        source?: string,
        lineno?: number,
        colno?: number,
        errorObject?: Error,
    ): boolean => {
        const err = errorObject || new Error(message.toString());
        this.c.logError(`Window.onerror triggered inside of the iframe. Src: ${source}:${lineno}:${colno}`, err);

        if (SAFEFRAME_ONERROR_COLLAPSE().isT1()) {
            // Check if the creative has begun to render properly, if not collapse the ad slot
            if (!evaluateCreativeBeginToRender(document.body).success) {
                this.c.collapseSlotInternal('CLIENTERROR');
            }
        } else {
            if (!this.renderCompleteTime) {
                this.c.collapseSlotInternal('CLIENTERROR');
            }
        }

        this.eventsChannel.onError(this.o);

        // Returning true suppresses the default browser behavior
        return true;
    };

    protected tryEncapsulateInWrapper(htmlContent: string): string {
        if (this.o.creativeWrapperDivEncoded) {
            this.c.countMetric('creativeWrapperDivEncoded', 1, true);
            const creativeWrapperDiv = convertToStringIfBase64(this.o.creativeWrapperDivEncoded);
            const creativeInsertPosition = creativeWrapperDiv.lastIndexOf('</div>');
            return [
                creativeWrapperDiv.slice(0, creativeInsertPosition),
                htmlContent,
                creativeWrapperDiv.slice(creativeInsertPosition),
            ].join('');
        }

        return htmlContent;
    }
}

// We generate our own synthetic load event for the creative to hook (since the initial sf script and html already fired it)
// we expect a matching call to generateSyntheticLoadEvent to be generated
const addSyntheticOnLoadListeners = (client: SafeframeClientImpl<any>, adDetails: AdDetails) => {
    if (!adDetails.creativeSupportsTTIMeasurement) {
        window.addEventListener('load', () => sendCriticalFeatureAndLoaded(client.c), { once: true });
    }
    window.addEventListener('load', () => client.c.fireViewableLatencyMetrics(), { once: true });
};
export const generateSyntheticLoadEvent = () => {
    window.dispatchEvent(new Event('load'));
};
